import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { UserTypology } from "helpers/Enums";
import { useIntl } from "react-intl";
import { LocalizationIds } from "helpers/Localizations";
import moment from "moment";

interface IUsersContext {
    recordCount: number
    search: ISearchRequest
    users: IUser[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (page: number) => void
    searchUsers: (searchRequest: ISearchRequest) => Promise<void>
    deleteUser: (userId: string) => Promise<void>
    updateSearch: (request: ISearchRequest) => void
    reSendEmail: (userEmail: string) => Promise<void>
}

export interface IUser {
    id: string
    email: string
    fullName: string
    language: string
    typology: UserTypology
    isDeleted: boolean
    createdOn: moment.Moment
}

interface ISearchRequest {
    searchText: string
    showOnly: 'active' | 'deleted'
    pageNr: number
    pageSize: number
}

export let customerUsersContext = React.createContext({} as IUsersContext);

let { Provider } = customerUsersContext;

let CustomerUsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();
    let { formatMessage } = useIntl();

    let [isLoading, setLoading] = useState(true);

    let [users, setUsers] = useState<IUser[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>({
        searchText: '',
        showOnly: 'active',
        pageNr: 0,
        pageSize: 15
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch(state.search);
            searchUsers(state.search);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let searchUsers = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/CustomerUser/SearchUsers', searchRequest);
            setUsers(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let deleteUser = async (userId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.DeleteUserConfirm }))) {
            try {
                await axios.delete(`/api/CustomerUser/DeleteUser/${userId}`)
                setUsers(users.filter(user => user.id !== userId));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar(formatMessage({ id: LocalizationIds.UserDeleted}), { variant: 'success' });
            } catch (error) { }
        }
    }

    let updateSearch = (request: ISearchRequest) => {
        setSearch(request);
    }

    let reSendEmail = async (customerId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.ResendEmailInvitationConfirm })) === false)
            return;

        try {
            await axios.get(`/api/CustomerUser/SendInvite/${customerId}`)
            enqueueSnackbar(formatMessage({id:LocalizationIds.CustomerInvited}), { variant: 'success' });
        } catch (error) { }
    }

    return (
        <Provider value={{
            recordCount,
            search,
            users,
            isLoading,

            doSearch,
            handleChangePage,
            searchUsers,
            deleteUser,
            updateSearch,
            reSendEmail
        }}>
            {children}
        </Provider>
    );
};

export default CustomerUsersProvider;